/**
 * Permanent-buff resolution.
 *
 * A handful of units care about buffs *as events* rather than about any
 * particular source of them:
 *
 *   - amplifiers  ("Permanent buffs on this unit are doubled")
 *   - self-riders ("Whenever this gains Attack or Health, also gain +1 Health")
 *   - watchers    ("Whenever a friendly unit gains permanent stats, gain +1 Attack")
 *
 * Every one of those needs a single choke point where a permanent stat
 * change is applied, which is what this module provides. Both consumers of
 * AbilityResults — `tavernAbilities.fireTavernTrigger` (immutable) and
 * `combatEngine` (mutates sim copies) — route their `buff` results through
 * `resolvePermanentBuff` and then apply the plan it returns.
 *
 * ## Why reactions do not recurse
 *
 * A watcher's own buff is itself a permanent stat gain, so feeding
 * reactions back through the resolver would loop forever the moment two
 * watchers shared a board (each one's gain re-triggering the other). One
 * level of reaction is therefore deliberate and load-bearing, not an
 * oversight: a buff produces reactions, and reactions produce nothing.
 */

import type { Card } from './data/cards/types';

/** Ability ids this module reads directly off cards (trigger: "passive"). */
const AMPLIFY = 'amplify_permanent_buffs';
const RIDER = 'bonus_on_gain_stats';
const WATCH = 'watch_friendly_gain_stats';

/** A single stat change to apply to one board slot. */
export interface BuffApplication {
  targetIndex: number;
  atk: number;
  hp: number;
}

export interface ResolvedBuff {
  /** The primary buff, after the target's own amplifiers/riders. */
  primary: BuffApplication;
  /** Watchers elsewhere on the board. Never amplified, never re-reacted. */
  reactions: BuffApplication[];
}

function abilitiesOf(card: Card | null | undefined, id: string) {
  if (!card?.abilities) return [];
  return card.abilities.filter((a) => a.id === id);
}

/**
 * Works out everything a permanent +atk/+hp on `targetIndex` should
 * actually do, without mutating anything.
 *
 * `rng` drives the chance-based amplifier (Chavo de las Rines' coin flip);
 * pass a seeded generator from the caller so combat stays deterministic.
 */
export function resolvePermanentBuff(
  board: (Card | null)[],
  targetIndex: number,
  atk: number,
  hp: number,
  rng: () => number = Math.random,
): ResolvedBuff {
  const target = board[targetIndex];
  const noop: ResolvedBuff = { primary: { targetIndex, atk, hp }, reactions: [] };
  if (!target) return noop;
  // A zero buff is not a gain — it must not wake watchers.
  if (atk === 0 && hp === 0) return noop;

  let finalAtk = atk;
  let finalHp = hp;

  // --- Amplifiers: scale the incoming buff ---
  for (const amp of abilitiesOf(target, AMPLIFY)) {
    const multiplier = amp.params?.multiplier ?? 1;
    const chance = amp.params?.chance;
    // `chance` omitted means "always". Roll once per amplifier.
    if (typeof chance === 'number' && rng() >= chance) continue;
    finalAtk = Math.floor(finalAtk * multiplier);
    finalHp = Math.floor(finalHp * multiplier);
  }

  // --- Riders: flat extra stats bolted onto any gain on this unit ---
  for (const rider of abilitiesOf(target, RIDER)) {
    // `requires: "health"` narrows to Health gains only.
    const requires = rider.params?.requires;
    if (requires === 'health' && hp <= 0) continue;
    if (requires === 'attack' && atk <= 0) continue;
    finalAtk += rider.params?.attack ?? 0;
    finalHp += rider.params?.health ?? 0;
  }

  // --- Watchers: other units reacting to a friendly gaining stats ---
  const reactions: BuffApplication[] = [];
  for (let i = 0; i < board.length; i++) {
    if (i === targetIndex) continue;
    const watcher = board[i];
    if (!watcher) continue;
    for (const w of abilitiesOf(watcher, WATCH)) {
      const requires = w.params?.requires;
      if (requires === 'health' && finalHp <= 0) continue;
      if (requires === 'attack' && finalAtk <= 0) continue;
      reactions.push({
        targetIndex: i,
        atk: w.params?.attack ?? 0,
        hp: w.params?.health ?? 0,
      });
    }
  }

  return { primary: { targetIndex, atk: finalAtk, hp: finalHp }, reactions };
}

/** Flattens a ResolvedBuff into the applications to write, primary first. */
export function buffApplications(resolved: ResolvedBuff): BuffApplication[] {
  return [resolved.primary, ...resolved.reactions].filter(
    (b) => b.atk !== 0 || b.hp !== 0,
  );
}
